import Backend from "src/core/checkout/models/backend";
import * as CartsApi from "../api/carts";
import * as OrdersApi from "../api/orders";
import autoBind from "auto-bind";
import TymberCart from "../common/models/cart";
import TymberOrder from "../common/models/order";
import get from "lodash/get";
import {FeatureToggles} from "../common/featureToggles";
import localStorage from "src/core/common/localStorage";
import {DeliveryTypes} from "../common/models/DeliveryType";
import ECommerceActions from "src/core/checkout/actions";
import DeliveriesBackend from "../deliveries/backend";
import Address from "../common/models/address";
import {makePayment} from "../payments/factory";
import makeOrderPayload from "./orderPayloadFactory";
import {asyncHandleError, handleError, getError} from "src/core/common/utils";
import {DeliveryModes} from "src/core/common/models/deliveryMode";
import {inventoryTypeState} from "src/core/inventories/useInventoryType";
import {Deliveries} from "src/core/deliveries/api";
import {isClient} from "src/server/utils/isClient";

class TymberBackend extends Backend {
  static cartKey = "CART";

  constructor(cartKeyPrefix, deliveryService, authenticationService) {
    super();
    this.cartKeyPrefix = cartKeyPrefix;
    this._deliveryService = deliveryService;
    this.authenticationService = authenticationService;
    autoBind(this);
  }

  getCartKey() {
    return `${this.cartKeyPrefix}/${TymberBackend.cartKey}`;
  }

  hasCart() {
    return !!localStorage.getItem(this.getCartKey());
  }

  loadCart(uuid) {
    const localStorageCartId = localStorage.getItem(this.getCartKey());
    const cartId = uuid || localStorage.getItem(this.getCartKey());

    if (cartId !== localStorageCartId) {
      localStorage.setItem(this.getCartKey(), cartId);
    }

    if (!cartId) {
      return Promise.reject();
    } else {
      return CartsApi.show(cartId).then(cartObject => new TymberCart(cartObject));
    }
  }

  saveCart = cart => {
    localStorage.setItem(this.getCartKey(), cart.getId());
  };

  async createOrder(cart, additionalInfo = {}) {
    const orderInfo = this.makeOrderPayload(cart, additionalInfo);
    const onlinePayment = makePayment(cart.getPaymentOption());
    const payload = await onlinePayment.preparePayment(
      orderInfo,
      additionalInfo.payment,
      cart.getId(),
      additionalInfo.isGuestCheckout
    );
    try {
      const order = await OrdersApi.create(cart, payload).then(
        orderData => new TymberOrder(orderData)
      );
      if (this.authenticationService.isLoggedIn()) {
        this.authenticationService.loadUserProfile();
      }
      return order;
    } catch (e) {
      console.log(e);
      await onlinePayment.maybeHandle(additionalInfo.payment, e);
      return null;
    }
  }

  async pay(order, additionalInfo = {}) {
    const onlinePayment = makePayment(additionalInfo.paymentOption);
    await onlinePayment.pay(order.getId(), additionalInfo.payment);
    return this.getOrder(order.getId());
  }

  makeOrderPayload = (cart, additionalInfo = {}) => {
    const featureToggles = FeatureToggles.getCurrentInstance();
    return makeOrderPayload({cart, additionalInfo, featureToggles});
  };

  clearCart() {
    localStorage.removeItem(this.getCartKey());
  }

  getOrder(orderId) {
    return OrdersApi.get(orderId).then(orderData => new TymberOrder(orderData));
  }

  getOrdersList(payload) {
    return OrdersApi.list(payload).then(ordersListData => ordersListData);
  }

  postOrderReview(payload) {
    return OrdersApi.review(payload).then(orderData => orderData);
  }

  clearCartIfAlreadySubmitted = e => {
    const handled = handleError("cart_already_submitted", e, () => null);
    if (handled) return null;

    throw e;
  };

  async addItem(cart, {product, quantity, weightKey}) {
    const payload = this.enhanceCart(
      {
        id: product.getId(),
        quantity: Math.max(1, quantity),
        weight_key: weightKey || undefined,
      },
      cart
    );
    const skipNotification = (e, NotificationsService) => {
      const error = getError("cart_already_submitted", e);
      if (error) {
        NotificationsService.warn(
          "You already submitted that cart. We created a new one for you."
        );
        return true;
      }
      return false;
    };
    return CartsApi.addItem(cart.getId(), payload, {skipNotification})
      .then(cartObject => cart.setCartObject(cartObject))
      .catch(async e => {
        const [handled, result] = await asyncHandleError(
          "cart_already_submitted",
          e,
          () => {
            return this.createCart({product, quantity, weightKey});
          }
        );
        if (handled) return result;

        throw e;
      });
  }

  deleteItem(cart, {product}) {
    return CartsApi.deleteItem(cart.getId(), {id: product.getId()})
      .then(cartObject => cart.setCartObject(cartObject))
      .catch(this.clearCartIfAlreadySubmitted);
  }

  updateItem(cart, item) {
    const payload = this.enhanceCart(
      {
        quantity: Math.max(1, item.getQuantity()),
        weight_key: item.getWeightKey() || undefined,
        id: item.getProductId(),
      },
      cart
    );
    return CartsApi.updateItem(cart.getId(), item.getId(), payload)
      .then(cartObject => cart.setCartObject(cartObject))
      .catch(this.clearCartIfAlreadySubmitted);
  }

  createCart({product, quantity, weightKey}) {
    const payload = this.enhanceCart({
      id: product.getId(),
      quantity: Math.max(1, quantity),
      weight_key: weightKey || undefined,
    });
    return CartsApi.create(payload).then(cartData => {
      const newCart = new TymberCart(cartData);
      this.saveCart(newCart);
      return newCart;
    });
  }

  validateCart(cart, additionalInfo) {
    const otherAttrs = this.enhanceCart({}, cart, additionalInfo?.deliverySpecChanged);
    return CartsApi.validate(cart, otherAttrs).then(cartPricesObject =>
      cart
        .setCartPrices(cartPricesObject)
        .setDeliverySpec(otherAttrs.delivery_specification)
    );
  }

  setPromoCodes(cart, promoCodes) {
    const otherAttrs = this.enhanceCart({}, cart);
    const newCart = cart.setPromoCodes(promoCodes || []);
    return CartsApi.update(newCart, otherAttrs)
      .then(cartObject => cart.setCartObject(cartObject))
      .catch(this.clearCartIfAlreadySubmitted);
  }

  setReward(cart, reward) {
    const otherAttrs = this.enhanceCart({}, cart);
    const newCart = cart.setReward(reward || null);
    return CartsApi.update(newCart, otherAttrs)
      .then(cartObject => cart.setCartObject(cartObject))
      .catch(this.clearCartIfAlreadySubmitted);
  }

  setSchedule(cart, schedule) {
    const newCart = cart.setSchedule(schedule);
    const otherAttrs = this.enhanceCart({}, newCart);
    if (schedule.code !== cart.getDeliverySpecification().getMode().code) {
      Deliveries.setDeliveryMode({mode: schedule.code});
      return Promise.resolve(newCart);
    } else {
      return CartsApi.update(newCart, otherAttrs)
        .then(cartObject => cart.setCartObject(cartObject))
        .catch(this.clearCartIfAlreadySubmitted);
    }
  }

  setPaymentOption(cart, paymentOption) {
    const otherAttrs = this.enhanceCart({payment_option: paymentOption}, cart);
    return CartsApi.update(cart, otherAttrs)
      .then(cartObject => cart.setCartObject(cartObject))
      .catch(this.clearCartIfAlreadySubmitted);
  }

  handleValidationErrors(currentCart, error) {
    const errors = get(error, "data.errors", null);
    if (!Array.isArray(errors)) return super.handleValidationErrors(currentCart, error);

    const cartError = errors.find(error => error.fields.indexOf("cart") > -1);
    const promoCodeError = errors.find(error => error.code === "invalid_promo_code");
    if (cartError && !promoCodeError) {
      return Promise.resolve(this.handleCartError(currentCart, error, cartError));
    } else if (promoCodeError) {
      const invalidCouponCodes = get(promoCodeError, "extra_info.promo_codes", []);
      let newCart;
      if (invalidCouponCodes.length === 0) {
        newCart = currentCart.setPromoCodes([]);
      } else {
        newCart = currentCart.setPromoCodes(
          currentCart.removePromoCodes(invalidCouponCodes)
        );
      }
      return CartsApi.update(newCart).then(cartObject => {
        newCart = newCart.setCartObject(cartObject);
        const otherAttrs = this.enhanceCart({}, newCart);
        return CartsApi.validate(newCart, otherAttrs).then(cartPricesObject => ({
          cart: newCart.setCartPrices(cartPricesObject),
          error,
        }));
      });
    }
  }

  handleCartError = (currentCart, error, cartError) => {
    const itemPrices = this.getPricesFromError(cartError);
    if (itemPrices) {
      const newCart = currentCart.setCartPrices(itemPrices);
      return {cart: newCart, error};
    } else {
      return {cart: currentCart, error};
    }
  };

  getPricesFromError = cartError => {
    const itemPrices = get(cartError, "extra_info.items", []);
    if (itemPrices.length) {
      return {
        data: {
          attributes: {},
        },
        included: itemPrices.map(item => ({
          id: item.uuid,
          type: "cart_item_prices",
          attributes: item,
        })),
      };
    } else {
      return null;
    }
  };

  withDeliverySpecification = (payload = {}, cart, updateDeliverySpec) => {
    let deliverySpec = updateDeliverySpec
      ? null
      : cart?.getDeliverySpecification()?.serialize();

    const deliveryType = this._deliveryService.getDeliveryType();
    const type = deliverySpec?.type || deliveryType?.code;
    const address =
      deliverySpec?.address || this._deliveryService.getDeliveryAddress()?.addressObject;

    let mode = deliverySpec?.mode || this._deliveryService.getDeliveryMode();
    const currentMode = cart?.getDeliverySpecification()?.getMode();
    const hasSchedule = currentMode?.getEndTime8601() && currentMode?.getStartTime8601();

    //TODO: possible issue - should only switch to ASAP is ASAP is enabled
    const switchToAsap =
      mode === DeliveryModes.SCHEDULED &&
      !deliverySpec &&
      (!this.isInCheckout() || !hasSchedule);

    if (switchToAsap) {
      mode = DeliveryModes.ASAP;
    }

    if (!type) return payload;

    deliverySpec = {
      ...deliverySpec,
      type: type,
      address: type === DeliveryTypes.DELIVERY ? address : undefined,
      mode,
      scheduled_start_time:
        mode === DeliveryModes.SCHEDULED ? currentMode?.getStartTime8601() : undefined,
      scheduled_end_time:
        mode === DeliveryModes.SCHEDULED ? currentMode?.getEndTime8601() : undefined,
    };

    return {
      ...payload,
      delivery_specification: deliverySpec,
    };
  };

  withInventoryType(payload) {
    const inventoryType = inventoryTypeState.get();
    inventoryTypeState.save(inventoryType);

    return {
      ...payload,
      inventory_type: inventoryTypeState.get(),
    };
  }

  enhanceCart = (payload = {}, cart, updateDeliverySpec = false) => {
    let result = this.withDeliverySpecification(payload, cart, updateDeliverySpec);
    return this.withInventoryType(result);
  };

  isInCheckout = () => {
    return isClient && window.location.pathname.indexOf("checkout") > -1;
  };
}

const addressChangeHandler = (function () {
  let previous = null;
  return addressObj => {
    const address = new Address(addressObj);
    const cart = ECommerceActions.getCart();
    if (
      !address.equals(cart?.getDeliverySpecification().getAddress()) ||
      !address.equals(previous)
    ) {
      ECommerceActions.validateCart({deliverySpecChanged: true});
    }
    previous = address;
  };
})();
const deliveryTypeChangeHandler = (function () {
  let previous = null;
  return deliveryType => {
    if (!previous || !deliveryType || deliveryType.code !== previous.code) {
      ECommerceActions.validateCart({deliverySpecChanged: true});
    }
    previous = deliveryType;
  };
})();
const deliveryModeChangeHandler = (function () {
  let previous = null;
  return deliveryMode => {
    if (deliveryMode !== previous) {
      ECommerceActions.validateCart({deliverySpecChanged: true});
    }
    previous = deliveryMode;
  };
})();
DeliveriesBackend.addEventListener("onSelectDeliveryAddress", addressChangeHandler);
DeliveriesBackend.addEventListener("onSelectDeliveryType", deliveryTypeChangeHandler);
DeliveriesBackend.addEventListener("onSelectDeliveryMode", deliveryModeChangeHandler);

export default TymberBackend;
